// src/components/SideBar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { MENU_INTERNAL, MENU_CLIENT, iconMap } from '../menus.js';
import './SideBar.css';

const SIDEBAR_EXPANDED_WIDTH = 200;
const SIDEBAR_COLLAPSED_WIDTH = 60;

function SideBar({
  isAdmin = false,
  isClient = false,
  showOrders = true,
  showInvoicing = true,
  showClients = true,
  showWarehouseFees = true,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // Collapsed on narrow screens
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);

  // Track which groups are open
  const [openMenus, setOpenMenus] = useState({
    invoices: false,
    admin: false,
    items: false,       // Items/SKUs group auto-open
    inventory: false,   // Inventory group auto-open
    warehouses: false,  // Support nested Warehouses group under Admin
    warehousefees: false, // Support nested Warehouse Fees group under Warehouses
    shipping: false,    // Support nested Shipping group under Admin
  });

  // Choose which menu model to use
  const model = isClient ? MENU_CLIENT : MENU_INTERNAL;

  // Apply admin filtering to groups
  const items = useMemo(() => {
    const shouldHide = (item = {}) => {
      if (!showOrders && item.path === '/orders') return true;
      if (!showClients && item.path === '/clients') return true;
      if (!showWarehouseFees) {
        if (item.key === 'warehousefees') return true;
        if (item.path && item.path.startsWith('/templates/')) return true;
      }
      if (!showInvoicing) {
        const rootPath = item.rootPath || (Array.isArray(item.children) && item.children.length > 0 && item.children[0].path) || '';
        const label = typeof item.label === 'string' ? item.label.toLowerCase() : '';
        const isInvoicesGroup =
          item.key === 'invoices' ||
          (rootPath && rootPath.startsWith('/invoices')) ||
          label.includes('invoic');
        if (item.path === '/invoices' || isInvoicesGroup) return true;
      }
      return false;
    };

    const filterList = (list = []) => {
      return (list || []).reduce((acc, item) => {
        if (!item || shouldHide(item)) return acc;
        if (item.type === 'group') {
          acc.push({ ...item, children: filterList(item.children || []) });
        } else {
          acc.push(item);
        }
        return acc;
      }, []);
    };
    const base = (model.items || []).filter(it => !it.requiresAdmin || isAdmin);
    return filterList(base);
  }, [model, isAdmin, showOrders, showInvoicing, showClients, showWarehouseFees]);

  useEffect(() => {
    const onResize = () => setCollapsed(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
    document.documentElement.style.setProperty('--sidebar-width', `${width}px`);
  }, [collapsed]);

  useEffect(() => () => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.removeProperty('--sidebar-width');
  }, []);

  // Auto-open groups based on current route
  useEffect(() => {
    setOpenMenus(prev => {
      const next = { ...prev };
      const pathname = location.pathname;

      // open the invoices group if on matching path
      if (pathname.startsWith('/invoices') || pathname.startsWith('/payments')) {
        next.invoices = true;
      }
      // open admin group if on users/settings/designer
      if (
        pathname.startsWith('/users') ||
        pathname.startsWith('/settings') ||
        pathname.startsWith('/suppliers') ||
        pathname.startsWith('/carriers') ||
        pathname.startsWith('/warehouse-designer') ||
        pathname.startsWith('/warehouse-config') ||
        pathname.startsWith('/warehouse') ||
        pathname.startsWith('/warehouses') ||
        pathname.startsWith('/shoppingintegrations') ||
        pathname.startsWith('/shippingintegrations') ||
        pathname.startsWith('/shipping-funding') ||
        pathname.startsWith('/shipping-packages') ||
        pathname.startsWith('/appconfig') ||
        pathname.startsWith('/templates/warehousefees') ||
        pathname.startsWith('/templates/receiptfees') ||
        pathname.startsWith('/templates/orderfees') ||
        pathname.startsWith('/templates/eorderfees') ||
        pathname.startsWith('/templates/customfees') ||
        pathname.startsWith('/server-status')
      ) {
        next.admin = true;
      }
      // Ensure nested Warehouses group opens for its routes
      if (
        pathname.startsWith('/warehouse-config') || pathname.startsWith('/warehouse') ||
        pathname.startsWith('/warehouses') ||
        pathname.startsWith('/warehouse-designer') ||
        pathname.startsWith('/templates/warehousefees') ||
        pathname.startsWith('/templates/receiptfees') ||
        pathname.startsWith('/templates/orderfees') ||
        pathname.startsWith('/templates/eorderfees') ||
        pathname.startsWith('/templates/customfees')
      ) {
        next.warehouses = true;
      }
      // Auto-open the Warehouse Fees subgroup for fee routes
      if (
        pathname.startsWith('/templates/warehousefees') ||
        pathname.startsWith('/templates/receiptfees') ||
        pathname.startsWith('/templates/orderfees') ||
        pathname.startsWith('/templates/eorderfees') ||
        pathname.startsWith('/templates/customfees')
      ) {
        next.warehousefees = true;
      }
      // Ensure nested Shipping group opens for its routes
      if (
        pathname.startsWith('/shippingintegrations') ||
        pathname.startsWith('/shipping-funding') ||
        pathname.startsWith('/shipping-packages')
      ) {
        next.shipping = true;
      }
      // NEW: open the items group if on /items or /bundles
      if (pathname.startsWith('/items') || pathname.startsWith('/bundles')) {
        next.items = true;
      }
      // NEW: open the inventory group if on /inventory or /picking-replenishment
      if (pathname.startsWith('/inventory') || pathname.startsWith('/picking-replenishment')) {
        next.inventory = true;
      }

      return next;
    });
  }, [location.pathname]);

  const toggleSidebar = () => setCollapsed(v => !v);

  const isActive = (path) => location.pathname === path;

  const onGroupClick = (key, rootPath) => (e) => {
    if (collapsed && rootPath) {
      navigate(rootPath);
      return;
    }
    e.preventDefault();
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderIcon = (name) => {
    const Icon = iconMap[name];
    return Icon ? <Icon /> : null;
    // fallback could be null if icon not found
  };

  const renderLink = (item, className = 'sidebar-link') => (
    <Link to={item.path} className={`${className} ${isActive(item.path) ? 'active' : ''}`}>
      <span className="icon">{renderIcon(item.icon)}</span>
      {!collapsed && <span className="label">{item.label}</span>}
    </Link>
  );

  const renderGroup = (group) => {
    const key = group.key;
    const rootPath =
      group.rootPath ||
      (group.children && group.children.length > 0 ? group.children[0].path : undefined);
    const open = !!openMenus[key];

    return (
      <li key={key} className={`sidebar-group ${open ? 'open' : ''}`}>
        <Link
          to={rootPath || '#'}
          className="sidebar-link group-link"
          onClick={onGroupClick(key, rootPath)}
        >
          <span className="icon">{renderIcon(group.icon)}</span>
          {!collapsed && <span className="label">{group.label}</span>}
          {!collapsed && (
            <span className="submenu-arrow">{open ? <FaChevronDown /> : <FaChevronRight />}</span>
          )}
        </Link>

        {!collapsed && open && (
          <ul className="submenu">
            {(group.children || []).map(child => (
              child.type === 'group'
                ? renderGroup(child)
                : (<li key={child.key || child.path}>{renderLink(child, 'sidebar-link submenu-link')}</li>)
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <img
          src={`${(import.meta.env.DEV ? '/' : (import.meta.env.VITE_ASSET_BASE || '/api/public/'))}easywms_logo.png`}
          alt="EasyWMS Logo"
          className="sidebar-logo"
        />
        <button className="toggle-btn" onClick={toggleSidebar} aria-label="Toggle Menu">
          <FaBars />
        </button>
      </div>

      <ul className="sidebar-links">
        {items.map((it) =>
          it.type === 'group' ? renderGroup(it) : (
            <li key={it.path}>{renderLink(it)}</li>
          )
        )}
      </ul>
    </div>
  );
}

export default SideBar;
